import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  DialogTitle,
  DialogActions,
  Container,
  Paper,
  Tab,
  Tabs,
} from "@mui/material";
import { Add, Close, Description, Visibility, Edit } from "@mui/icons-material";
import axios from "axios";
import { useToast } from "../../components/Toast";
import { useNavigate, useParams } from "react-router-dom";
import { useFirebaseAuth } from "../../hooks/useFirebaseAuth";
import { Loading } from "../../components/Loading";
import { uploadFile } from "../../utils/UploadFile";
import { LocalUser } from "../../utils/UploadFile";
import { fetchDocuments } from "../../utils/FetchDocuments";
import { DocumentUploadSection } from "../../components/DocumentUploadSection";
import { UploadedFile } from "../../types/loan";
import NavigationHeader from "../../components/NavigationHeader";
import { commonDateFieldProps, commonTextFieldProps } from "../../utils/styles";
import { formatDate } from "../../utils/utils";

interface Permit {
  id: string;
  permitType: string;
  stateName: string;
  issuingAuthority: string;
  permitStartDate: string;
  permitEndDate: string;
  isActive: boolean;
  documents?: UploadedFile[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const PermitTable = ({
  permits,
  handleEditPermit,
  handlePreview,
}: {
  permits: Permit[];
  handleEditPermit: (permit: Permit) => void;
  handlePreview: (url: string) => void;
}) => {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={tableCellStyles}>Permit Type</TableCell>
            <TableCell sx={tableCellStyles}>State</TableCell>
            <TableCell sx={tableCellStyles}>Issuing Authority</TableCell>
            <TableCell sx={tableCellStyles}>Start Date</TableCell>
            <TableCell sx={tableCellStyles}>End Date</TableCell>
            <TableCell sx={tableCellStyles}>Status</TableCell>
            <TableCell sx={tableCellStyles}>Document</TableCell>
            <TableCell sx={tableCellStyles}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {permits?.map((permit) => (
            <TableRow key={permit.id}>
              <TableCell sx={tableCellStyles}>{permit.permitType}</TableCell>
              <TableCell sx={tableCellStyles}>{permit.stateName}</TableCell>
              <TableCell sx={tableCellStyles}>
                {permit.issuingAuthority}
              </TableCell>
              <TableCell sx={tableCellStyles}>
                {formatDate(permit.permitStartDate)}
              </TableCell>
              <TableCell sx={tableCellStyles}>
                {formatDate(permit.permitEndDate)}
              </TableCell>
              <TableCell sx={tableCellStyles}>
                <Chip
                  label={permit.isActive ? "Active" : "Expired"}
                  color={permit.isActive ? "success" : "error"}
                  size="small"
                />
              </TableCell>
              <TableCell sx={tableCellStyles}>
                {permit.documents && permit.documents.length > 0 ? (
                  <>
                    {permit.documents.map((document) => (
                      <Box
                        key={document.id}
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Description
                          color="primary"
                          sx={{ width: "15px", height: "15px" }}
                        />
                        <Typography variant="body2" sx={tableCellStyles}>
                          {document.fileName}
                        </Typography>
                        <IconButton
                          onClick={() => handlePreview(document.presignedUrl)}
                          size="small"
                          sx={{ color: "primary.main", ml: "auto" }}
                        >
                          <Visibility sx={{ width: "15px", height: "15px" }} />
                        </IconButton>
                      </Box>
                    ))}
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={tableCellStyles}
                  >
                    No document
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={tableCellStyles}>
                <IconButton
                  onClick={() => handleEditPermit(permit)}
                  sx={{ color: "primary.main" }}
                >
                  <Edit sx={{ width: "15px", height: "15px" }} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {(!permits || !permits?.length) && (
            <TableRow>
              <TableCell colSpan={8} align="center">
                No permits found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const VehiclePermit = () => {
  const { vehicleId } = useParams();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { user, loading } = useFirebaseAuth();
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : "";
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmittingPermit, setIsSubmittingPermit] = useState(false);
  const [activePermits, setActivePermits] = useState<Permit[]>([]);
  const [expiredPermits, setExpiredPermits] = useState<Permit[]>([]);
  const [selectedPermit, setSelectedPermit] = useState<Permit | null>(null);
  const [permitForm, setPermitForm] = useState({
    permitType: "Select",
    stateName: "",
    issuingAuthority: "",
    permitStartDate: "",
    permitEndDate: "",
    otherPermitType: "",
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedFile[]>(
    []
  );
  const [isUploading, setIsUploading] = useState(false);
  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (user && vehicleId && !loading) {
      fetchPermits();
    }
  }, [user, vehicleId, loading]);

  const fetchPermits = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}vehicle-data/permits/vehicle/${vehicleId}`,
        {
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      if (response?.data?.isSuccess) {
        const permitsData = response?.data?.data;
        // Fetch documents for each permit
        const permitsWithDocs = await Promise.all(
          permitsData.map(async (permit: Permit) => {
            const docs = await fetchDocuments({
              documentContext: "vehicle",
              documentTypeId: permit.id,
              documentType: "vehicle-permit",
            });
            return {
              ...permit,
              documents: docs.map((doc) => ({
                id: doc.id,
                fileName: doc.fileName,
                presignedUrl: doc.presignedUrl,
              })),
            };
          })
        );
        setActivePermits(permitsWithDocs.filter((permit) => permit.isActive));
        setExpiredPermits(permitsWithDocs.filter((permit) => !permit.isActive));
      } else {
        showToast("Failed to fetch permits", "error");
      }
    } catch (error) {
      console.error("Error fetching permits:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePermitTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPermitForm((prev) => ({
      ...prev,
      permitType: e.target.value,
      otherPermitType: e.target.value === "Other" ? prev.otherPermitType : "",
    }));
    if (e.target.value === "AIP" || e.target.value === "National") {
      setPermitForm((prev) => ({
        ...prev,
        stateName: "INDIA",
      }));
    }
  };

  const handlePermitDetailChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: string
  ) => {
    setPermitForm((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handlePreview = (url: string) => {
    window.open(url, "_blank");
  };

  const handleEditPermit = (permit: Permit) => {
    setSelectedPermit(permit);
    setPermitForm({
      permitType: permit.permitType,
      stateName: permit.stateName,
      issuingAuthority: permit.issuingAuthority,
      permitStartDate: new Date(permit.permitStartDate)
        .toISOString()
        .split("T")[0],
      permitEndDate: new Date(permit.permitEndDate).toISOString().split("T")[0],
      otherPermitType: permit.permitType === "Other" ? permit.permitType : "",
    });
    if (permit.documents && permit.documents.length > 0) {
      setUploadedDocuments(permit.documents);
    }
    setIsDialogOpen(true);
  };

  const handlePermitSubmit = async () => {
    setIsSubmittingPermit(true);
    try {
      const enrichedPermitForm = {
        ...permitForm,
        vehicleId: vehicleId || "",
      };

      let response;
      if (selectedPermit) {
        // Update existing permit
        response = await axios.put(
          `${process.env.REACT_APP_API_HOST}vehicle-data/permits/${selectedPermit.id}`,
          enrichedPermitForm,
          {
            headers: {
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
      } else {
        // Create new permit
        response = await axios.post(
          `${process.env.REACT_APP_API_HOST}vehicle-data/permits`,
          enrichedPermitForm,
          {
            headers: {
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
      }

      if (response?.data?.isSuccess) {
        if (selectedFiles.length > 0) {
          let promises = [];
          for (const file of selectedFiles) {
            promises.push(
              uploadFile({
                file,
                documentType: "vehicle-permit",
                documentContext: "vehicle",
                description: permitForm.permitType,
                documentTypeId: selectedPermit
                  ? selectedPermit.id
                  : response.data.data.id,
                companyId,
              })
            );
          }
          await Promise.all(promises);
        }
        showToast(
          `Permit ${selectedPermit ? "updated" : "added"} successfully`,
          "success"
        );
        setIsDialogOpen(false);
        setSelectedFiles([]);
        setSelectedPermit(null);
        setPermitForm({
          permitType: "Select",
          stateName: "",
          issuingAuthority: "",
          permitStartDate: "",
          permitEndDate: "",
          otherPermitType: "",
        });
        fetchPermits();
      } else {
        showToast(
          `Failed to ${selectedPermit ? "update" : "add"} permit`,
          "error"
        );
      }
    } catch (error) {
    } finally {
      setIsSubmittingPermit(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedFiles([]);
    setSelectedPermit(null);
    setPermitForm({
      permitType: "Select",
      stateName: "",
      issuingAuthority: "",
      permitStartDate: "",
      permitEndDate: "",
      otherPermitType: "",
    });
    setUploadedDocuments([]);
  };

  if (loading || isLoading) {
    return <Loading message={`Loading permits...`} fullScreen={true} />;
  }

  if (isSubmittingPermit) {
    return (
      <Loading
        message={`${selectedPermit ? "Updating" : "Submitting"} permit...`}
        fullScreen={true}
      />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Vehicles", path: "/vehicles" },
          { label: "Vehicle Details", path: `/vehicles/${vehicleId}` },
          { label: "Vehicle Permits" },
        ]}
      />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Vehicle Permits</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          sx={{ px: 1, py: 0.5, fontSize: "0.8rem" }}
          onClick={() => setIsDialogOpen(true)}
        >
          Add New Permit
        </Button>
      </Box>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
            >
              <Tab
                sx={{ fontSize: "0.8rem" }}
                label="Active Permits"
                {...a11yProps(0)}
              />
              <Tab
                sx={{ fontSize: "0.8rem" }}
                label="Expired Permits"
                {...a11yProps(1)}
              />
            </Tabs>
          </Box>
          <CustomTabPanel value={value} index={0}>
            <PermitTable
              permits={activePermits}
              handleEditPermit={handleEditPermit}
              handlePreview={handlePreview}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <PermitTable
              permits={expiredPermits}
              handleEditPermit={handleEditPermit}
              handlePreview={handlePreview}
            />
          </CustomTabPanel>
        </Box>
      </Paper>

      {/* Permit Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ fontSize: "1rem" }}
          >
            {selectedPermit ? "Edit Permit" : "Add New Permit"}
            <IconButton onClick={handleCloseDialog} size="small" title="Close">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Permit Type"
                value={permitForm.permitType}
                onChange={handlePermitTypeChange}
                fullWidth
                disabled={!!selectedPermit}
                {...commonTextFieldProps}
              >
                <MenuItem value="Select" disabled>
                  Select
                </MenuItem>
                <MenuItem value="PUC">PUC</MenuItem>
                <MenuItem value="AIP">AIP</MenuItem>
                <MenuItem value="National">National</MenuItem>
                <MenuItem value="State">State</MenuItem>
                <MenuItem value="Tourist">Tourist</MenuItem>
                <MenuItem value="Fitness">Fitness</MenuItem>
                <MenuItem value="Home Tax">Home Tax</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>
            {permitForm.permitType === "Other" && !selectedPermit && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Specify Permit Type"
                  value={permitForm.otherPermitType}
                  onChange={(e) =>
                    handlePermitDetailChange(
                      e as React.ChangeEvent<HTMLInputElement>,
                      "otherPermitType"
                    )
                  }
                  fullWidth
                  required
                  {...commonTextFieldProps}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                label={
                  permitForm.permitType === "AIP" ||
                  permitForm.permitType === "National"
                    ? "Region"
                    : "State Name"
                }
                value={permitForm.stateName}
                onChange={(e) =>
                  handlePermitDetailChange(
                    e as React.ChangeEvent<HTMLInputElement>,
                    "stateName"
                  )
                }
                fullWidth
                disabled={
                  permitForm.permitType === "AIP" ||
                  permitForm.permitType === "National"
                }
                {...commonTextFieldProps}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Issuing Authority"
                value={permitForm.issuingAuthority}
                onChange={(e) =>
                  handlePermitDetailChange(
                    e as React.ChangeEvent<HTMLInputElement>,
                    "issuingAuthority"
                  )
                }
                fullWidth
                required
                {...commonTextFieldProps}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                label="Permit Start Date"
                value={permitForm.permitStartDate}
                onChange={(e) =>
                  handlePermitDetailChange(
                    e as React.ChangeEvent<HTMLInputElement>,
                    "permitStartDate"
                  )
                }
                // InputLabelProps={{ shrink: true }}
                fullWidth
                required
                {...commonDateFieldProps}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                label="Permit End Date"
                value={permitForm.permitEndDate}
                onChange={(e) =>
                  handlePermitDetailChange(
                    e as React.ChangeEvent<HTMLInputElement>,
                    "permitEndDate"
                  )
                }
                // InputLabelProps={{ shrink: true }}
                fullWidth
                required
                {...commonDateFieldProps}
              />
            </Grid>
            <Grid item xs={12}>
              <DocumentUploadSection
                title={`${
                  selectedPermit ? "Update" : "Upload"
                } Permit Document`}
                selectedFiles={selectedFiles}
                uploadedFiles={uploadedDocuments}
                onFileSelect={(files: File[]) => {
                  setSelectedFiles(files);
                }}
                onFileRemove={(index: number) =>
                  setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
                }
                onPreview={handlePreview}
                isLoading={isUploading}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            disabled={isSubmittingPermit}
            sx={{ fontSize: "1rem", height: "30px" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePermitSubmit}
            disabled={isSubmittingPermit}
            sx={{ fontSize: "1rem", height: "30px" }}
          >
            {isSubmittingPermit
              ? `${selectedPermit ? "Updating" : "Submitting"}...`
              : selectedPermit
              ? "Update"
              : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VehiclePermit;

const tableCellStyles = {
  fontSize: "0.7rem",
};
